import throttle from 'lodash/throttle';
import { applyMiddleware, compose, createStore } from 'redux';
import thunk from 'redux-thunk';
import { isServerSide } from 'helpers/ssr';
import { prefillStore } from '@common/Core/actions';
import { STATE_CACHE_NAME } from '@common/Core/constants/cache';
import { rootReducer } from '@common/Core/reducers';
import { FAVORITE_PRODUCTS_CACHE_NAME, favoriteBrandsCacheName } from '@common/Favorites/constants/cache';
import { hasInvalidDataFormat } from '@common/Product/helpers';
import * as cookieHelper from '@helpers/cookie';
import { parseJSON } from '@helpers/data';
import { loadState, saveState } from '@helpers/localStorage';
import hooks from './hooks';
import reducers from './reducers';

const getStore = (props) => {
    // Load state from cookie
    const stateFromCookie = parseJSON(cookieHelper.get(STATE_CACHE_NAME), {});
    const stateFromLocalStorage = loadState(STATE_CACHE_NAME);

    if (stateFromLocalStorage?.favorites) {
        const hasFavorites = stateFromLocalStorage.favorites?.products?.length > 0;

        if (hasFavorites && !hasInvalidDataFormat(stateFromLocalStorage.favorites.products)) {
            saveState(FAVORITE_PRODUCTS_CACHE_NAME, stateFromLocalStorage.favorites.products);
        }

        const hasFavoriteBrands = stateFromLocalStorage.favorites?.brands
            && Object.keys(stateFromLocalStorage.favorites.brands)
                .find(gender => stateFromLocalStorage.favorites.brands[gender].length > 0, []);

        if (hasFavoriteBrands) {
            saveState(favoriteBrandsCacheName, stateFromLocalStorage.favorites.brands);
        }

        delete stateFromLocalStorage.favorites;
    }

    // Load state from localStorage and sessionStorage
    let persistedState = { ...stateFromLocalStorage, ...stateFromCookie };

    // Redux middleware
    const middlewares = [thunk];

    const composeMiddlewares = () => {
        if (process.LOGGER) {
            // eslint-disable-next-line global-require
            const { createLogger } = require('redux-logger');
            const loggerMiddleware = createLogger();

            middlewares.push(loggerMiddleware);

            if ('__REDUX_DEVTOOLS_EXTENSION__' in window) {
                return compose(
                    applyMiddleware(...middlewares),
                    window.__REDUX_DEVTOOLS_EXTENSION__()
                );
            }
        }

        return applyMiddleware(...middlewares);
    };

    if (props) {
        persistedState = { ...persistedState, ...props };
    }

    // Store / state
    const store = createStore(rootReducer(reducers), persistedState, composeMiddlewares());

    if (typeof App !== 'undefined') {
        App.Component = App.Component || {};

        // Hooks to dispatch actions from old jQuery app
        hooks();
    }

    // Update localStorage and sessionStorage (only save data we want to persist between page loads)
    store.subscribe(throttle(() => {
        const { activateDeals } = store.getState();

        const stateCookie = {
            listing: {
                gridView: store.getState().listing.gridView,
                limit: store.getState().listing.limit
            },
            activateDeals: { activated: activateDeals?.activated || [] }
        };

        saveState(STATE_CACHE_NAME, {
            dialog: store.getState().dialog
        });

        cookieHelper.set(STATE_CACHE_NAME, JSON.stringify(stateCookie));
    }, 250));

    return store;
};

let storeInstance;

export default function getStoreInstance(props) {
    if (storeInstance && !isServerSide()) {
        if (typeof props !== 'undefined') {
            prefillStore(props)(storeInstance.dispatch);
        }

        return storeInstance;
    }

    storeInstance = getStore(props);

    return storeInstance;
}
